import { AreaChart, Area, CartesianGrid, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import type { MeasurementEntry } from '@tally/lib/types';

type Row = Record<string, number | string | null | undefined>;

const LATEST_COLOR = '#34d399';

function lastPopulatedIndex(data: Row[], dataKey: string): number {
  for (let i = data.length - 1; i >= 0; i--) {
    const v = data[i][dataKey];
    if (v != null && v !== '') return i;
  }
  return -1;
}

function formatValue(v: unknown, digits: number): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '';
  return digits === 0 ? Math.round(v).toString() : v.toFixed(digits);
}

function makeDotRenderer(dataKey: string, color: string, lastIndex: number, labelDigits: number) {
  return (props: { cx?: number; cy?: number; index?: number; payload?: Row }) => {
    const { cx, cy, index, payload } = props;
    if (cx == null || cy == null || index == null) return <g />;
    if (payload && payload[dataKey] == null) return <g />;
    const isLast = index === lastIndex;
    if (!isLast) {
      return (
        <circle cx={cx} cy={cy} r={1.8} fill={color} />
      );
    }
    const label = payload ? formatValue(payload[dataKey], labelDigits) : '';
    return (
      <g>
        <text
          x={cx}
          y={cy - 10}
          textAnchor="middle"
          fontFamily="JetBrains Mono"
          fontSize={10}
          fill={LATEST_COLOR}
        >
          {label}
        </text>
        <circle
          cx={cx}
          cy={cy}
          r={4.5}
          fill={LATEST_COLOR}
          stroke="#0c0a09"
          strokeWidth={2}
        />
      </g>
    );
  };
}

export function ChartCard({
  title,
  dataKey,
  data,
  color,
  domain,
  labelDigits = 1,
}: {
  title: string;
  dataKey: string;
  data: Row[];
  color: string;
  domain?: [string | number, string | number];
  labelDigits?: number;
}) {
  const lastIndex = lastPopulatedIndex(data, dataKey);
  return (
    <div className="bg-stone-900 border border-stone-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-mono">{title}</div>
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] font-mono text-stone-500">
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: LATEST_COLOR }} />
          latest
        </div>
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={data} margin={{ top: 18, right: 8, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#292524" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fill: '#78716c', fontSize: 10, fontFamily: 'JetBrains Mono' }}
            axisLine={{ stroke: '#44403c' }}
            tickLine={false}
            interval={Math.max(1, Math.floor(data.length / 5))}
          />
          <YAxis
            domain={domain as [number, number] | undefined}
            tick={{ fill: '#78716c', fontSize: 10, fontFamily: 'JetBrains Mono' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{ background: '#0c0a09', border: '1px solid #44403c', fontFamily: 'JetBrains Mono', fontSize: 11 }}
            labelStyle={{ color: '#a8a29e' }}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#grad-${dataKey})`}
            dot={makeDotRenderer(dataKey, color, lastIndex, labelDigits)}
            activeDot={{ r: 5, stroke: '#0c0a09', strokeWidth: 2 }}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Body-measurement trend. Reads from the unified `measurements` log so
 * same-day updates don't erase earlier values from the trend line.
 */
export function MeasurementChartCard({
  title,
  log,
  color,
}: {
  title: string;
  log: MeasurementEntry[];
  color: string;
}) {
  const data: Row[] = log.map((e) => ({
    label: e.date.slice(5),
    value: e.value_cm,
  }));
  const lastIndex = data.length - 1;
  const gradId = `grad-measurement-${title.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="bg-stone-900 border border-stone-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-mono">
          {title} · cm <span className="text-stone-600">· {log.length} log{log.length === 1 ? '' : 's'}</span>
        </div>
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] font-mono text-stone-500">
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: LATEST_COLOR }} />
          latest
        </div>
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={data} margin={{ top: 18, right: 8, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#292524" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: '#78716c', fontSize: 10, fontFamily: 'JetBrains Mono' }}
            axisLine={{ stroke: '#44403c' }}
            tickLine={false}
            interval={Math.max(0, Math.floor(data.length / 5))}
          />
          <YAxis
            domain={['dataMin - 0.5', 'dataMax + 0.5']}
            tick={{ fill: '#78716c', fontSize: 10, fontFamily: 'JetBrains Mono' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{ background: '#0c0a09', border: '1px solid #44403c', fontFamily: 'JetBrains Mono', fontSize: 11 }}
            labelStyle={{ color: '#a8a29e' }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#${gradId})`}
            dot={makeDotRenderer('value', color, lastIndex, 1)}
            activeDot={{ r: 5, stroke: '#0c0a09', strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DualChart({ title, data }: { title: string; data: Row[] }) {
  return (
    <div className="bg-stone-900 border border-stone-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-mono">{title}</div>
        <div className="flex gap-3 text-[9px] uppercase tracking-[0.2em] font-mono">
          <span className="text-emerald-400">● intake</span>
          <span className="text-rose-400">● burn</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid stroke="#292524" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fill: '#78716c', fontSize: 10, fontFamily: 'JetBrains Mono' }}
            axisLine={{ stroke: '#44403c' }}
            tickLine={false}
            interval={Math.max(1, Math.floor(data.length / 5))}
          />
          <YAxis tick={{ fill: '#78716c', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{ background: '#0c0a09', border: '1px solid #44403c', fontFamily: 'JetBrains Mono', fontSize: 11 }}
            labelStyle={{ color: '#a8a29e' }}
          />
          <Line type="monotone" dataKey="intake" stroke="#34d399" strokeWidth={1.5} dot={false} connectNulls />
          <Line type="monotone" dataKey="whoop" stroke="#fb7185" strokeWidth={1.5} dot={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
